import type { OrderPriorityTimeframe } from '@seen/core/models/orders/constants'
import { cn, IconUrgent } from '@seen/ui'
import type { JSX } from 'react'
import { match } from 'ts-pattern'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'

export interface OrderPriorityIconProps {
  priority: OrderPriorityTimeframe | null | undefined
  className?: string
  tooltip?: string
}

export function OrderPriorityIcon({
  priority,
  className,
  tooltip,
}: OrderPriorityIconProps): JSX.Element {
  const icon = match(priority)
    .with(null, undefined, () => (
      <svg
        aria-label="No Priority"
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="lch(39.48% 1 282.863 / 1)"
        role="img"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="1.5"
          y="7.25"
          width="3"
          height="1.5"
          rx="0.5"
          opacity="0.9"
        ></rect>
        <rect
          x="6.5"
          y="7.25"
          width="3"
          height="1.5"
          rx="0.5"
          opacity="0.9"
        ></rect>
        <rect
          x="11.5"
          y="7.25"
          width="3"
          height="1.5"
          rx="0.5"
          opacity="0.9"
        ></rect>
      </svg>
    ))
    .with('stat-1', () => (
      <IconUrgent className={cn('fill-orange-500', className)} />
    ))
    .with('urgent-7', () => (
      <svg
        aria-label="High Priority"
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="lch(39.48% 1 282.863 / 1)"
        role="img"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
        <rect x="6.5" y="5" width="3" height="9" rx="1"></rect>
        <rect x="11.5" y="2" width="3" height="12" rx="1"></rect>
      </svg>
    ))
    .with('asap-14', () => (
      <svg
        aria-label="Medium Priority"
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="lch(39.48% 1 282.863 / 1)"
        role="img"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
        <rect x="6.5" y="5" width="3" height="9" rx="1"></rect>
        <rect
          x="11.5"
          y="2"
          width="3"
          height="12"
          rx="1"
          fillOpacity="0.4"
        ></rect>
      </svg>
    ))
    .with('routine-30', 'routine-90', 'routine-180', 'routine-365', () => (
      <svg
        aria-label="Low Priority"
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="lch(39.48% 1 282.863 / 1)"
        role="img"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
        <rect
          x="6.5"
          y="5"
          width="3"
          height="9"
          rx="1"
          fillOpacity="0.4"
        ></rect>
        <rect
          x="11.5"
          y="2"
          width="3"
          height="12"
          rx="1"
          fillOpacity="0.4"
        ></rect>
      </svg>
    ))
    .otherwise((value) => {
      // Handle legacy priority values (e.g., 'urgent-30', 'asap-30')
      const priorityPrefix = (value as string | null | undefined)?.split('-')[0]

      if (priorityPrefix === 'stat') {
        return <IconUrgent className={cn('fill-orange-500', className)} />
      }

      if (priorityPrefix === 'urgent') {
        return (
          <svg
            aria-label="High Priority"
            className={className}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="lch(39.48% 1 282.863 / 1)"
            role="img"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
            <rect x="6.5" y="5" width="3" height="9" rx="1"></rect>
            <rect x="11.5" y="2" width="3" height="12" rx="1"></rect>
          </svg>
        )
      }

      if (priorityPrefix === 'asap') {
        return (
          <svg
            aria-label="Medium Priority"
            className={className}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="lch(39.48% 1 282.863 / 1)"
            role="img"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
            <rect x="6.5" y="5" width="3" height="9" rx="1"></rect>
            <rect
              x="11.5"
              y="2"
              width="3"
              height="12"
              rx="1"
              fillOpacity="0.4"
            ></rect>
          </svg>
        )
      }

      // Default to low priority for 'routine' or unknown values
      return (
        <svg
          aria-label="Low Priority"
          className={className}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="lch(39.48% 1 282.863 / 1)"
          role="img"
          focusable="false"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="1.5" y="8" width="3" height="6" rx="1"></rect>
          <rect
            x="6.5"
            y="5"
            width="3"
            height="9"
            rx="1"
            fillOpacity="0.4"
          ></rect>
          <rect
            x="11.5"
            y="2"
            width="3"
            height="12"
            rx="1"
            fillOpacity="0.4"
          ></rect>
        </svg>
      )
    })

  if (!tooltip) {
    return icon
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{icon}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}
